'use client';

import {
  BubbleOverlay,
  BubbleQuickReplies,
  BubbleTimeSlots,
  type SubpageHeroBubble,
} from '@/components/sections/subpage-hero';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'See it in action',
    heading: "The agent we'd bet $2M on.",
    subtitle:
      'Open resolves real customer conversations end-to-end across every channel — voice, chat, email, WhatsApp, social.',
    agentName: 'Open Agent',
    bubbleUser1: 'Can I move my callback to tomorrow morning?',
    bubbleAgent1: 'Of course — pick a slot that works:',
    confirmLabel: 'Confirming…',
    bubbleUser2: 'Also, can I get a refund on order #4521?',
    bubbleAgent2: 'Sure. What should we do?',
    slotFull: 'Full refund',
    slotCredit: 'Store credit',
    slotReplace: 'Replace item',
    slotDay: 'May 13',
  },
  ar: {
    eyebrow: 'شوفه وهو يشتغل',
    heading: 'الوكيل اللي نراهن عليه بـ ٧,٥ مليون ⃁.',
    subtitle:
      'يحلّ Open محادثات عملائك الحقيقية من بدايتها إلى نهايتها، وعلى كل قناة — الصوت والدردشة والإيميل وWhatsApp وسوشيال.',
    agentName: 'وكيل Open',
    bubbleUser1: 'أقدر أأجّل المكالمة لبكرة الصبح؟',
    bubbleAgent1: 'أبشر — اختر الوقت اللي يناسبك:',
    confirmLabel: 'جارٍ التأكيد…',
    bubbleUser2: 'وشي ثاني، أقدر أسترجع فلوس الطلب #4521؟',
    bubbleAgent2: 'تمام. وش تبي نسوي؟',
    slotFull: 'استرجاع كامل',
    slotCredit: 'رصيد في المتجر',
    slotReplace: 'استبدال المنتج',
    slotDay: '١٣ مايو',
  },
} as const;

/**
 * The "proof" section — single autoplaying product video with the same
 * cycling chat-bubble overlay used on /channels/chat, dressed for the
 * guarantee page's dark background.
 */
export const ProofSection = () => {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  const BUBBLES: SubpageHeroBubble[] = [
    { role: 'user', text: c.bubbleUser1 },
    {
      role: 'agent',
      text: c.bubbleAgent1,
      widget: (
        <BubbleTimeSlots
          day={c.slotDay}
          slots={['08:00', '08:30', '09:00', '09:30', '10:00']}
          selected="08:00"
          confirmLabel={c.confirmLabel}
        />
      ),
    },
    { role: 'user', text: c.bubbleUser2 },
    {
      role: 'agent',
      text: c.bubbleAgent2,
      widget: (
        <BubbleQuickReplies
          options={[c.slotFull, c.slotCredit, c.slotReplace]}
          selected={c.slotFull}
        />
      ),
    },
  ];

  return (
    <section className="border-t border-white/5 py-20 md:py-24">
      <div className="container">
        <div className="mb-10 max-w-2xl text-start md:mb-12">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-amber-400">
            <span className="inline-flex h-1 w-1 rounded-full bg-amber-400" />
            {c.eyebrow}
          </p>
          <h2 className="font-serif text-balance text-white text-2xl md:text-3xl">
            {c.heading}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-400 md:text-base">
            {c.subtitle}
          </p>
        </div>
      </div>

      {/* Visual card – breaks out beyond the text container */}
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-8">
        <div
          className="relative w-full overflow-hidden rounded-[2.5rem] bg-neutral-950 ring-1 ring-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] md:rounded-[3rem]"
          style={{ aspectRatio: '21/9' }}
        >
          <video
            src="/home/features/chat.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            className="block h-full w-full object-cover"
          />

          <BubbleOverlay bubbles={BUBBLES} agentName={c.agentName} />
        </div>
      </div>
    </section>
  );
};

export default ProofSection;
